import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { systemSettingsApiService } from '../services/systemSettingsApiService';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuthContext } from './AuthContext';
import { debugError, debugLog } from '../utils/debug';
import type { CalcVersionAvailability } from '../types/calcVersion';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

export interface SystemSettingsContextType {
    manualSaveEnabled: boolean;
    siteOfflineEnabled: boolean;
    /** BillingDunning: days a past-due team keeps full access before it becomes read-only. */
    billingGraceDays: number;
    /** Which state/year calc combinations users may pick. Empty while loading or on error. */
    calcVersions: CalcVersionAvailability[];
    isLoading: boolean;
    error: Error | null;
    /** Reflect an admin's own toggle immediately in this session without a full reload. */
    setManualSaveEnabledLocal: (enabled: boolean) => void;
    /** Reflect an admin's own toggle immediately in this session without a full reload. */
    setSiteOfflineEnabledLocal: (enabled: boolean) => void;
    /** Reflect an admin's own change immediately in this session without a full reload. */
    setBillingGraceDaysLocal: (days: number) => void;
    /** Reflect an admin's own Calculations change immediately in this session without a full reload. */
    setCalcVersionEnabledLocal: (stateCode: string, year: number, isEnabled: boolean) => void;
}

/** Server default for BillingGraceDays when the setting has never been saved. */
export const DEFAULT_BILLING_GRACE_DAYS = 14;

// ============================================================================
// CONTEXT CREATION
// ============================================================================

export const SystemSettingsContext = createContext<SystemSettingsContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export interface SystemSettingsProviderProps {
    children: ReactNode;
}

export const SystemSettingsProvider: React.FC<SystemSettingsProviderProps> = ({ children }) => {
    const { isAuthenticated } = useAuthContext();
    const [manualSaveEnabled, setManualSaveEnabled] = useState<boolean>(false);
    const [siteOfflineEnabled, setSiteOfflineEnabled] = useState<boolean>(false);
    const [billingGraceDays, setBillingGraceDays] = useState<number>(DEFAULT_BILLING_GRACE_DAYS);
    const [calcVersions, setCalcVersions] = useState<CalcVersionAvailability[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    // This provider mounts once at the app root and outlives login/logout, but the settings
    // endpoint requires auth. Refetch on every isAuthenticated flip (not just on mount) so a
    // fetch that 401'd pre-login doesn't leave these stuck at their fail-closed defaults for
    // the rest of the session once the user actually logs in.
    useEffect(() => {
        if (!isAuthenticated) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const loadSystemSettings = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const settings = await systemSettingsApiService.getSystemSettings();
                if (cancelled) return;
                setManualSaveEnabled(settings.manualSaveEnabled);
                setSiteOfflineEnabled(settings.siteOfflineEnabled);
                setBillingGraceDays(typeof settings.billingGraceDays === 'number' ? settings.billingGraceDays : DEFAULT_BILLING_GRACE_DAYS);
                setCalcVersions(Array.isArray(settings.calcVersions) ? settings.calcVersions : []);
                debugLog('[SystemSettingsProvider] System settings loaded', { settings });
            } catch (err) {
                if (cancelled) return;
                const error = err instanceof Error ? err : new Error('Unknown error loading system settings');
                debugError('[SystemSettingsProvider] Error loading system settings:', error);
                setError(error);
                // On error, fail closed — Manual Save stays disabled (autosave keeps working).
                setManualSaveEnabled(false);
                setSiteOfflineEnabled(false);
                setCalcVersions([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadSystemSettings();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const setManualSaveEnabledLocal = useCallback((enabled: boolean) => {
        setManualSaveEnabled(enabled);
    }, []);

    const setSiteOfflineEnabledLocal = useCallback((enabled: boolean) => {
        setSiteOfflineEnabled(enabled);
    }, []);

    const setBillingGraceDaysLocal = useCallback((days: number) => {
        setBillingGraceDays(days);
    }, []);

    const setCalcVersionEnabledLocal = useCallback((stateCode: string, year: number, isEnabled: boolean) => {
        setCalcVersions(prev => {
            const exists = prev.some(v => v.stateCode === stateCode && v.year === year);
            return exists
                ? prev.map(v => (v.stateCode === stateCode && v.year === year ? { ...v, isEnabled } : v))
                : [...prev, { stateCode, year, isEnabled }];
        });
    }, []);

    return (
        <SystemSettingsContext.Provider value={{ manualSaveEnabled, siteOfflineEnabled, billingGraceDays, calcVersions, isLoading, error, setManualSaveEnabledLocal, setSiteOfflineEnabledLocal, setBillingGraceDaysLocal, setCalcVersionEnabledLocal }}>
            {children}
        </SystemSettingsContext.Provider>
    );
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access system settings context
 * @throws Error if used outside of SystemSettingsProvider
 */
export function useSystemSettingsContext(): SystemSettingsContextType {
    const context = useContext(SystemSettingsContext);
    if (context === undefined) {
        throw new Error('useSystemSettingsContext must be used within a SystemSettingsProvider');
    }
    return context;
}

/** True if an admin has enabled Manual Save (DB-backed, global). Does not check the ManualSaveToggle feature flag. */
export function useManualSaveEnabled(): boolean {
    return useSystemSettingsContext().manualSaveEnabled;
}

/** True if the site is currently locked to Admin/Root users only (DB-backed, global). */
export function useSiteOfflineEnabled(): boolean {
    return useSystemSettingsContext().siteOfflineEnabled;
}

/**
 * Effective Manual Save mode: requires BOTH the config-only `ManualSaveToggle` feature flag
 * (gates whether the capability exists at all) AND the DB-backed, admin-toggled
 * `ManualSaveEnabled` system setting (the runtime on/off switch). See
 * docs/AUTOSAVE_DETAILS.md "Manual Save mode" section.
 */
export function useManualSaveMode(): boolean {
    const flagEnabled = useFeatureFlag('ManualSaveToggle');
    const settingEnabled = useManualSaveEnabled();
    return flagEnabled && settingEnabled;
}
